/*
  In-memory projection of write descriptors.

  The server applies writes to Postgres; a browser applying the same command optimistically has to
  apply them to whatever it holds in memory. Same descriptors, same handler, two interpreters — which
  is what keeps an optimistic apply and the authoritative one from disagreeing.

  Deliberately narrow: this projects the decision writes onto a bucket map, which is the slice the
  walking-skeleton client keeps. It is not a general in-memory database, and it says so by returning
  `unprojected` rather than dropping writes it does not understand — a client that silently ignored a
  head or override write would show a stale chart and never find out.
*/
import { DEFAULT_BUCKET } from '@integrate/contracts';
export function projectDecisionWrites(decisions, writes) {
    const next = { ...decisions };
    const unprojected = new Set();
    for (const write of writes) {
        /*
          `scenarioPatch` used to be exempted here, on the grounds that it "carries no per-person effect a
          bucket map can show". True of the bucket map, and the wrong conclusion — the client renders far more
          than the bucket map, and the descriptor carries `disabledLevels`, which decides the SHAPE of the
          chart, plus `perimeterThreshold`, which the slider and every rule badge read. So a colleague
          flattening a level left the other tab drawing the old tree, with nothing to put it right.
        */
        if (write.t !== 'decision') {
            unprojected.add(write.t);
            continue;
        }
        if (write.patch === null) {
            delete next[write.employeeId];
            continue;
        }
        const status = write.patch.status;
        if (status === undefined) {
            /*
              The patch touched the stage or the stranded plan and not the bucket, which a bucket map has
              nowhere to put — so this says so instead of skipping, and the caller refetches.
      
              It used to `continue` silently, and the consequence was the whole point of `unprojected` inverted:
              a colleague's Day 1 transfer, Day 2 exit or stranded plan — and any undo of one — arrived, bumped
              the sequence number, appended to the activity list and changed NOTHING on screen. The panels read
              those fields out of `bucketMeta` and `strandedPlan`, which only a refetch replaces, and because
              the sequence number had advanced a reconnect would not replay it either.
            */
            unprojected.add('decision');
            continue;
        }
        /*
          A bucket change, by contrast, IS fully representable, and that is a property of the design rather
          than a hope. `bucketPatch` gives a fresh carve-out entrant `src: 'manual'` and
          `joinStage: 'perimeter'`, and `bucketMetaFor` falls back to exactly those two when it finds no
          entry — and returns null outright for anybody the bucket map no longer says is in the carve-out, so
          a stale entry left behind by a departure is never read. The metadata the patch also carries is
          therefore the metadata the reader would infer anyway.
        */
        if (status === null || status === DEFAULT_BUCKET)
            delete next[write.employeeId];
        else
            next[write.employeeId] = status;
    }
    return { decisions: next, unprojected: [...unprojected] };
}
/**
 * Lift a bucket map into the decision slice a handler reads.
 *
 * Lossy by construction — a bucket map carries no stage attribution or stranded plan — so it is only
 * enough for the commands a client applies optimistically. A handler needing more will reject, which
 * is the right outcome: the client falls back to the server's answer rather than guessing.
 */
export function decisionsFromBuckets(buckets) {
    const out = Object.create(null);
    for (const [employeeId, status] of Object.entries(buckets)) {
        out[employeeId] = {
            status,
            statusSrc: 'manual',
            joinStage: status === 'Carve-out' ? 'perimeter' : null,
            day2Exit: null,
            strandedAction: null,
            strandedMonth: null,
        };
    }
    return out;
}
//# sourceMappingURL=projection.js.map